import type { FC } from "react";

import { CRM_LIST_PAGE_ROUTE } from "app/pages/sales-intelligence/constants/routes";
import EmailAlertButton from "app/pages/sales-intelligence/modules/lists/components/EmailAlertButton/EmailAlertButton";
import ListItem from "app/pages/sales-intelligence/modules/lists/components/ListItem/ListItem";
import { StyledListItemsContainer } from "app/pages/sales-intelligence/modules/lists/containers/saved-searches/SavedSearchesContainer/styles";
import {
    StyledCrmIcon,
    StyledCRMListContainer,
} from "app/pages/sales-intelligence/modules/lists/sub-modules/crm-list/containers/CRMListsContainer/styles";
import useCRMSignalsAlertsFrequencyMutation from "app/pages/sales-intelligence/modules/lists/sub-modules/crm-list/mutations/useCRMListsSignalsAlertsFrequencyMutation";
import type { CrmListDto } from "app/pages/sales-intelligence/modules/lists/types/dto";
import { StyledAutoCreatedTag } from "app/pages/sales-intelligence/sub-modules/opportunities/components/list-items/OpportunityListItem/styles";
import { getCrmListName } from "app/pages/sales-intelligence/sub-modules/opportunities/helpers/crmList";
import signalsAlertsTrackingService from "app/pages/sales-intelligence/sub-modules/signals-alerts/services/tracking/trackingService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type CRMListsContainerProps = {
    records: CrmListDto[];
};
const CRMListsContainer: FC<CRMListsContainerProps> = ({ records }) => {
    const alertsFrequencyMutation = useCRMSignalsAlertsFrequencyMutation();

    const handleClick = (item: Readonly<CrmListDto>) => {
        const { objectType, integrationName, syncedDomainsCount } = item;

        if (syncedDomainsCount === 0) {return;}

        proNavigator.go(CRM_LIST_PAGE_ROUTE, { crm: integrationName, objectType });
    };

    const handleAlertsToggle = (item: Readonly<CrmListDto>) => {
        const { integrationName } = item;
        const isPaused = item.digestSubscription?.status === "paused";
        const newStatus = isPaused ? "active" : "paused";
        const assetType = integrationName === "hubspot" ? "hubspotList" : "salesforceList";
        const assetId = `${integrationName}-${item.objectType}`;

        signalsAlertsTrackingService.trackAlertFrequencySelect(newStatus === "active" ? "Weekly" : "Paused");
        alertsFrequencyMutation.mutate({
            assetId,
            assetType,
            status: newStatus,
        });
    };

    return (
        <StyledCRMListContainer>
            <StyledListItemsContainer>
                {records.map((item: CrmListDto, index: number) => {
                    const { integrationName, syncedDomainsCount, lastUpdated, objectType } = item;

                    return (
                        <ListItem
                            key={index}
                            name={getCrmListName(integrationName, objectType)}
                            icon={
                                <StyledCrmIcon
                                    iconName={`${integrationName}-colored`}
                                    size="md"
                                />
                            }
                            onClick={() => handleClick(item)}
                            tag={<StyledAutoCreatedTag />}
                            lastUpdated={lastUpdated ? new Date(lastUpdated) : undefined}
                            websitesCount={syncedDomainsCount}
                            metadataElements={[
                                <EmailAlertButton
                                    key={`email-alert-button-${integrationName}-${objectType}`}
                                    isPaused={item.digestSubscription?.status === "paused"}
                                    onChange={() => handleAlertsToggle(item)}
                                />,
                            ]}
                        />
                    );
                })}
            </StyledListItemsContainer>
        </StyledCRMListContainer>
    );
};

export default CRMListsContainer;
